import { memo, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { EntityModalActions } from 'actions';
import { ANALYTICS_REPORT } from 'constants/Entities';
import { FORCEBI } from 'constants/Features';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import EntityMenu from 'containers/components/EntityMenu';
import SearchMetrics from './SearchMetrics';

const MetricsMenu = ({
    onChange,
    pathItemSelected,
    pathItems,
    onCreateFolder,
    onSearch,
    sectionSelected,
}) => {
    const dispatch = useDispatch();
    const analyticsPro = useSelector((state) => state.config?.permission?.analyticsPro);
    const analyticsProCreateDashboard = useSelector(
        (state) => state.config?.permission?.analyticsProCreateDashboard || false,
    );
    const hasFORCEBI = useSelector((state) => state.config.flagsmith?.[FORCEBI] || false);

    const canCreateDashboard = useMemo(() => {
        return analyticsPro && analyticsProCreateDashboard && hasFORCEBI;
    }, [analyticsPro, analyticsProCreateDashboard, hasFORCEBI]);

    const addOptions = useMemo(() => {
        let addButtonOptions = [];
        if (onCreateFolder)
            addButtonOptions.push({
                onClick: onCreateFolder,
                title: getLiteral('action_add_folder'),
                description: getLiteral('label_analytics_pro_new_folder_desc'),
                icon: 'newFolder',
            });

        if (canCreateDashboard && sectionSelected === 'dashboards') {
            addButtonOptions.push({
                onClick: () =>
                    dispatch(
                        EntityModalActions.init({
                            entity: ANALYTICS_REPORT,
                            trackingSubmodule: 'forceBI',
                            data: {
                                folderId: pathItemSelected?.id,
                                openDetail: true,
                            },
                            labels: {
                                title: getLiteral('label_create_dashboard'),
                                success: getLiteral('succes_entitycreatedsuccessfully'),
                                error: getLiteral('label_failed_create'),
                            },
                            version: 2,
                        }),
                    ),
                title: getLiteral('label_analytics_pro_new_report'),
                description: getLiteral('label_analytics_pro_new_report_desc'),
                icon: 'newReport',
            });

            addButtonOptions.push({
                onClick: () => {
                    logEvent({
                        event: ANALYTICS_REPORT.trueName,
                        submodule: 'powerBI',
                        functionality: 'create',
                    });
                    dispatch(
                        EntityModalActions.init({
                            entity: ANALYTICS_REPORT,
                            trackingSubmodule: 'externalDashboard',
                            data: {
                                folderId: pathItemSelected?.id,
                                provider: 5,
                                openDetail: true,
                            },
                            labels: {
                                title: getLiteral('label_new_external_dashboard'),
                                success: getLiteral('succes_entitycreatedsuccessfully'),
                                error: getLiteral('label_failed_create'),
                            },
                            version: 2,
                        }),
                    );
                },
                title: getLiteral('label_new_external_dashboard'),
                description: getLiteral('label_new_external_dashboard_desc'),
                icon: 'newReport',
            });
        }

        if (!addButtonOptions.length) return null;

        return {
            addButtonOptions,
        };
    }, [canCreateDashboard, dispatch, onCreateFolder, pathItemSelected?.id, sectionSelected]);

    const canCreate = useMemo(() => {
        return sectionSelected === 'dashboards' && (!!onCreateFolder || canCreateDashboard);
    }, [canCreateDashboard, onCreateFolder, sectionSelected]);

    return (
        <div className="fm-metrics">
            <EntityMenu
                withBreadcrumb
                canCreate={canCreate}
                folderSelected={pathItemSelected}
                folderSelectedArr={pathItems}
                onClickBreadcrumb={onChange}
                searchComponent={<SearchMetrics onSearch={onSearch} />}
                {...addOptions}
            />
        </div>
    );
};

export default memo(MetricsMenu);
